import { yupResolver } from '@hookform/resolvers/yup';
import { UseMutationResult } from '@tanstack/react-query';
import React, { useRef, useState, useEffect, KeyboardEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useSnackbar } from '@/alerts/AlertProvider';
import { useAppConfigContext } from '@/context/AppConfig';
import {
  CreateListResponse,
  useAccountLists,
} from '@/dashboard/AccountLists/useAccountLists/useAccountLists';
import { useAccountListsV2 } from '@/dashboard/AccountListsV2/hooks/useAccountListsV2';
import { AddReportToListV2AddressPayload } from '@/reports/types/serialized';
import { Button, Grid, Text, Icons, Divider, Input } from '@/theme/index';
import { ValidReportTypes } from '@/utils/helperTypes';
import { useRedirect } from '@/utils/hooks/useRedirect';
import { schemaListForm } from '@/utils/schemaForms/schemaForms';
import { LizanoListInfo } from '../lists/lists';

interface ListParams {
  name: string;
  listCode?: string;
  permalink: string;
}

interface formProps {
  listName: string;
}
interface ListFormProps {
  closeForm: () => void;
  reportTitle?: string;
  reportType?: ValidReportTypes;
  reportData?: AddReportToListV2AddressPayload;
  permalink?: string;
  title: string;
  mainActionButtonText: string;
  placeholderText: string;
  listCode?: string;
  action: UseMutationResult<
    CreateListResponse | LizanoListInfo,
    unknown,
    ListParams,
    unknown
  >;
}

const listNameLimit = 100;

export function ListForm({
  closeForm,
  reportTitle,
  reportType,
  reportData,
  permalink,
  title,
  mainActionButtonText,
  placeholderText,
  listCode,
  action,
}: ListFormProps) {
  const { trackEvent } = useAppConfigContext();
  const { redirect } = useRedirect();
  const { currentListNames } = useAccountLists();
  const { addReportToListV2 } = useAccountListsV2();
  const [createdListName, setCreatedListName] = useState('');
  const [listNameCurrentLength, setListNameCurrentLength] = useState(0);
  const [loadingState, setLoadingState] = useState(false);
  const [success, setSuccess] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const listInput = useRef<HTMLInputElement | null>(null);
  const {
    handleSubmit,
    register,
    clearErrors,
    formState: { errors },
    reset,
    setError,
    setValue,
  } = useForm({
    resolver: yupResolver(schemaListForm),
    defaultValues: {
      listName: '',
    },
  });

  const isPropertyReport = reportType && reportType === 'property';

  const showCreatedList = () => {
    setSuccess(true),
      setCreatedListName(listInput.current?.value || 'new list');
    enqueueSnackbar('List created successfully', {
      variant: 'success',
    });
    setTimeout(() => {
      closeForm();
      if (listInput.current) reset();
      setLoadingState(false);
      setSuccess(false);
      setCreatedListName('');
      action.reset();
    }, 5000);
  };

  const handleAddReportToListV2 = (
    createdListData: LizanoListInfo | CreateListResponse
  ) => {
    if (!reportData) return;
    const typedcreatedListData = createdListData as CreateListResponse;
    const newCreatedListCode = typedcreatedListData?.data?.list?.list_code;

    if (newCreatedListCode) {
      addReportToListV2.mutate({
        list_code: newCreatedListCode,
        property: reportData,
      });
    }
  };

  const onSubmit = async ({ listName }: formProps) => {
    const listNameTrimmed = listName.trim().toLocaleLowerCase();
    if (currentListNames?.includes(listNameTrimmed)) {
      setError('listName', { message: 'List name already exists' });
      return;
    }
    const params = {
      name: listNameTrimmed,
      listCode,
      permalink: permalink || 'default',
    };
    trackEvent('Add to New List', 'engagement click', 'rats component');
    setLoadingState(true);
    action.mutate(params, {
      onSuccess: (response) => {
        if (isPropertyReport) {
          handleAddReportToListV2(response);
        }
      },
    });
  };

  const handleCancelClick = () => {
    clearErrors('listName');
    closeForm();
    reset();
  };

  const handleRedirectClick = () => {
    // Currently in FHub this redirects and pre-selects the list in
    redirect('/dashboard/lists');
  };

  useEffect(() => {
    if (action.isSuccess) {
      if (!listCode) showCreatedList();
      setLoadingState(false);
      clearErrors('listName');
      setListNameCurrentLength(0);
      closeForm();
      action.reset();
    }
  }, [action.isSuccess]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const { value } = event.target as HTMLInputElement;
      setValue('listName', value);
      handleSubmit(onSubmit)();
    }
  };

  return (
    <form
      onClick={(event) => event.stopPropagation()}
      onSubmit={handleSubmit(onSubmit)}
    >
      {success ? (
        <Grid
          container
          columnSpacing={2}
          sx={{ width: '287px', padding: (theme) => theme.spacing(2) }}
        >
          <Grid item xs={12}>
            <Text variant="subtitle1" color="primary">
              "{reportTitle}" has been <br />
              added to:
            </Text>
            <Grid container alignItems="center">
              <Icons.Check fontSize="small" color="primary" />
              <Text variant="subtitle1" color="primary">
                <b>{createdListName}</b>
              </Text>
            </Grid>
          </Grid>
          <Grid item xs={12} justifyContent="center">
            <Divider
              color="secondary"
              sx={{
                width: '100%',
                borderWidth: '1px',
                paddingLeft: '16px',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              onClick={handleRedirectClick}
              type="submit"
              color="success"
              sx={{ flex: '0 0 auto', padding: 0 }}
            >
              View list
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Button
              color="success"
              onClick={handleCancelClick}
              sx={{ flex: '0 0 auto', padding: 0 }}
            >
              Continue
            </Button>
          </Grid>
        </Grid>
      ) : (
        <Grid
          container
          columnSpacing={2}
          sx={{ width: '287px', padding: (theme) => theme.spacing(2) }}
        >
          <Grid item xs={12}>
            <Text variant="subtitle1" color="primary">
              {title}
            </Text>
          </Grid>
          <Grid item xs={12}>
            <Input
              {...register('listName')}
              type="text"
              placeholder={placeholderText}
              inputRef={listInput}
              inputProps={{ maxLength: 100, onKeyDown: handleKeyDown }}
              onChange={(e: { target: { value: string } }) =>
                setListNameCurrentLength(e.target.value.length)
              }
              fullWidth
              sx={{ marginRight: (theme) => theme.spacing(1) }}
            />
          </Grid>
          <Grid item xs={12} textAlign="end">
            <Text variant="subtitle2" color="primary">
              {listNameCurrentLength}/{listNameLimit}
            </Text>
          </Grid>
          <Grid item xs={12} textAlign="start">
            <Text variant="subtitle2" color="error">
              {errors.listName?.message}
            </Text>
          </Grid>
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="success"
              fullWidth
              sx={{ flex: '0 0 auto' }}
              disabled={loadingState}
            >
              {mainActionButtonText}
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Button
              color="success"
              onClick={handleCancelClick}
              fullWidth
              sx={{ flex: '0 0 auto', textDecoration: 'underline' }}
            >
              Cancel
            </Button>
          </Grid>
        </Grid>
      )}
    </form>
  );
}
